import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../services/api';
import AnnouncePanel from '../components/AnnouncePanel';
import GroupPictureModerationPanel from '../components/GroupPictureModerationPanel';
import PhotoVerifications from './PhotoVerifications';

const REMOVAL_REASONS = [
  { id: 'explicit_content', label: 'Explicit Content' },
  { id: 'nudity', label: 'Nudity' },
  { id: 'harassment', label: 'Harassment / Bullying' },
  { id: 'spam', label: 'Spam' },
  { id: 'hate_speech', label: 'Hate Speech' },
  { id: 'violence', label: 'Violence / Graphic' },
  { id: 'misinformation', label: 'Misinformation' },
  { id: 'privacy_violation', label: 'Privacy violation / sensitive information' },
  { id: 'other', label: 'Other' },
];

function CommunityModeration() {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const adminRole = (localStorage.getItem('adminRole') || 'SUPER_ADMIN').toUpperCase();
  const canReviewProfilePictures = ['SUPER_ADMIN', 'VERIFICATION_ADMIN'].includes(adminRole);
  const canModerateContent = ['SUPER_ADMIN', 'CONTENT_MODERATOR'].includes(adminRole);
  const [activeTab, setActiveTab] = useState(
    requestedTab === 'profile-pictures' && canReviewProfilePictures ? 'profile-pictures' :
      requestedTab === 'group-pictures' && canModerateContent ? 'group-pictures' :
        requestedTab === 'announce' && canModerateContent ? 'announce' :
          canModerateContent ? 'feed' : 'profile-pictures'
  );
  const focusedPostId = searchParams.get('postId') || '';
  const [posts, setPosts] = useState([]);
  const [focusedPost, setFocusedPost] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  // Modal state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [removeReason, setRemoveReason] = useState('explicit_content');
  const [customMessage, setCustomMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Scan state
  const [scanningPostId, setScanningPostId] = useState(null);
  const [scanResults, setScanResults] = useState({});

  const fetchStats = useCallback(async () => {
    try {
      const res = await adminAPI.getCommunityStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      if (focusedPostId) {
        const res = await adminAPI.getCommunityPost(focusedPostId);
        setFocusedPost(res.data.post || null);
        setPosts(res.data.post ? [res.data.post] : []);
        setTotalPages(1);
      } else {
        setFocusedPost(null);
        const res = await adminAPI.getCommunityPosts({ page, limit: 20, filter });
        setPosts(res.data.posts || []);
        setTotalPages(res.data.pagination?.pages || 1);
      }
    } catch (err) {
      setFocusedPost(null);
      setError(err.response?.data?.error || 'Failed to load posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter, focusedPostId]);

  useEffect(() => { if (canModerateContent) fetchStats(); }, [fetchStats, canModerateContent]);
  useEffect(() => { if (canModerateContent) fetchPosts(); }, [fetchPosts, canModerateContent]);

  const openRemoveModal = (post) => {
    setSelectedPost(post);
    setRemoveReason('explicit_content');
    setCustomMessage('');
    setShowRemoveModal(true);
  };

  const handleRemovePost = async () => {
    if (!selectedPost) return;
    setActionLoading(true);
    try {
      await adminAPI.removeCommunityPost(selectedPost._id, {
        reason: removeReason,
        customMessage: customMessage || undefined,
      });
      setShowRemoveModal(false);
      setSelectedPost(null);
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove post');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePermanentDeletePost = async (post) => {
    if (!post?._id) return;
    const confirm = window.confirm(
      'Permanently delete this rejected post and its images from database and S3? This cannot be undone.'
    );
    if (!confirm) return;

    setActionLoading(true);
    try {
      await adminAPI.permanentlyDeleteCommunityPost(post._id, { hardDeleteMedia: true });
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to permanently delete post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestorePost = async (postId) => {
    if (!window.confirm('Restore this post? It will be visible in the feed again.')) return;
    try {
      await adminAPI.restoreCommunityPost(postId);
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to restore post');
    }
  };

  const handleScanPost = async (postId) => {
    setScanningPostId(postId);
    try {
      const res = await adminAPI.scanCommunityPost(postId);
      setScanResults(prev => ({ ...prev, [postId]: res.data.results }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to scan post');
    } finally {
      setScanningPostId(null);
    }
  };

  const handleApproveImage = async (postId, imageId) => {
    try {
      await adminAPI.approveCommunityPostImage(postId, imageId, { reason: 'Approved by moderator' });
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve image');
    }
  };

  const handleRejectImage = async (postId, imageId) => {
    try {
      await adminAPI.rejectCommunityPostImage(postId, imageId, { reason: 'Rejected by moderator' });
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject image');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900">Community Moderation</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Review feed content, profile and group pictures, and member communications.
        </p>
      </div>

      <div className="flex gap-2 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {canModerateContent && <button type="button" onClick={() => setActiveTab('feed')} className={`px-5 py-2 rounded-lg font-semibold ${activeTab === 'feed' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600'}`}>Feed Moderation</button>}
        {canReviewProfilePictures && <button type="button" onClick={() => setActiveTab('profile-pictures')} className={`px-5 py-2 rounded-lg font-semibold ${activeTab === 'profile-pictures' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600'}`}>Profile Pictures</button>}
        {canModerateContent && <button type="button" onClick={() => setActiveTab('group-pictures')} className={`px-5 py-2 rounded-lg font-semibold ${activeTab === 'group-pictures' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600'}`}>Group Pictures</button>}
        {canModerateContent && <button type="button" onClick={() => setActiveTab('announce')} className={`px-5 py-2 rounded-lg font-semibold ${activeTab === 'announce' ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600'}`}>Announce</button>}
      </div>

      {activeTab === 'announce' ? <AnnouncePanel /> : activeTab === 'profile-pictures' ? <PhotoVerifications embedded /> : activeTab === 'group-pictures' ? <GroupPictureModerationPanel /> : <>

      {focusedPostId && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-blue-900">
            <strong>Focused post from Feed Reports</strong>
            <span className="ml-2 font-mono text-xs">{focusedPostId}</span>
            {focusedPost?.author?._id && (
              <div className="mt-1">
                Author:{' '}
                <Link className="text-pink-600 font-semibold hover:underline" to={`/users/${focusedPost.author._id}`}>
                  {focusedPost.author.profileName || focusedPost.author.username || 'User'}
                </Link>
                {focusedPost.author.username ? (
                  <span className="text-gray-600"> @{focusedPost.author.username}</span>
                ) : null}
              </div>
            )}
          </div>
          <Link
            to="/community-moderation"
            className="px-3 py-2 bg-white border rounded-lg text-sm font-semibold hover:bg-gray-50"
          >
            Clear focus
          </Link>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Total Posts" value={stats.stats.totalPosts} />
          <StatCard label="Active" value={stats.stats.activePosts} color="green" />
          <StatCard label="Pending Review" value={stats.stats.pendingReviewPosts} color="orange" />
          <StatCard label="Rejected Queue" value={stats.stats.rejectedPosts} color="red" />
          <StatCard label="User Self-Deletes" value={stats.stats.userRemovals} color="gray" />
          <StatCard label="Total Images" value={stats.stats.totalImages} color="blue" />
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap gap-4 items-center mb-6">
        <div className="flex gap-2 bg-gray-100 rounded-xl p-1">
          {[
            { id: 'all', label: 'All Posts' },
            { id: 'with_images', label: 'With Images' },
            { id: 'active', label: 'Active' },
            { id: 'pending_review', label: 'Pending Review' },
            { id: 'rejected', label: 'Rejected Queue' },
            { id: 'removed', label: 'Deleted' },
          ].map(f => (
            <button
              key={f.id}
              type="button"
              onClick={() => { setFilter(f.id); setPage(1); }}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                filter === f.id ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>
      )}

      {/* Posts List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No posts found for this filter.</div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <PostCard
              key={post._id}
              post={post}
              onRemove={() => {
                if (post.moderationStatus === 'REJECTED_PENDING_MANUAL_REVIEW') {
                  handlePermanentDeletePost(post);
                } else {
                  openRemoveModal(post);
                }
              }}
              onRestore={() => handleRestorePost(post._id)}
              onScan={() => handleScanPost(post._id)}
              onApproveImage={handleApproveImage}
              onRejectImage={handleRejectImage}
              scanning={scanningPostId === post._id}
              scanResult={scanResults[post._id]}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center items-center gap-4 mt-6">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
          className="px-4 py-2 border rounded-xl disabled:opacity-50"
        >
          Previous
        </button>
        <span className="font-semibold">Page {page} of {totalPages}</span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => setPage(p => p + 1)}
          className="px-4 py-2 border rounded-xl disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Remove Modal */}
      {showRemoveModal && selectedPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Remove Post</h2>
              <p className="text-sm text-gray-500 mt-1">
                This will move the post to rejected moderation queue (hidden from app) and notify the user.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Author:</strong> {selectedPost.author?.profileName || 'Unknown'} ({selectedPost.author?.email || 'N/A'})
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Content:</strong> "{selectedPost.content}"
                </p>
                {selectedPost.images?.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {selectedPost.images.map(img => (
                      <img
                        key={img._id}
                        src={img.url}
                        alt="post"
                        className="w-20 h-20 object-cover rounded-lg border"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Removal Reason</label>
                <select
                  value={removeReason}
                  onChange={e => setRemoveReason(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm"
                >
                  {REMOVAL_REASONS.map(r => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Custom Message (optional, overrides default notification text)
                </label>
                <textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  rows={3}
                  placeholder="Leave empty to use the default message for the selected reason..."
                  className="w-full border rounded-lg p-2 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRemoveModal(false)}
                  className="flex-1 py-2 border rounded-xl font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRemovePost}
                  disabled={actionLoading}
                  className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 disabled:opacity-50"
                >
                  {actionLoading ? 'Removing...' : 'Remove & Notify User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

function StatCard({ label, value, color = 'pink' }) {
  const colorMap = {
    pink: 'bg-pink-50 text-pink-700 border-pink-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colorMap[color] || colorMap.pink}`}>
      <div className="text-2xl font-bold">{value?.toLocaleString() ?? '—'}</div>
      <div className="text-xs font-medium mt-1">{label}</div>
    </div>
  );
}

function PostCard({ post, onRemove, onRestore, onScan, onApproveImage, onRejectImage, scanning, scanResult }) {
  const isRemoved = post.isDeleted;
  const moderationStatus = post.moderationStatus || 'APPROVED';
  const isPendingReview = moderationStatus === 'PENDING_REVIEW';
  const isRejectedQueue = moderationStatus === 'REJECTED_PENDING_MANUAL_REVIEW';

  const humanDecisionSummary = (r) => {
    if (r?.error) return `Scan failed: ${r.error}`;

    const decision = r?.decision || 'APPROVE';
    const reason = r?.policyDecision?.reason;

    if (reason) return reason;
    if (decision === 'REJECT') return 'Rejected due to high-risk explicit content indicators.';
    if (decision === 'REVIEW') return 'Needs manual review due to suggestive/borderline signals.';
    return 'Approved. No policy violations matched required thresholds.';
  };

  const actionHint = (r) => {
    if (r?.error) return 'Retry scan after checking moderation service health.';
    if (r?.decision === 'REJECT') return 'Recommended action: Keep hidden unless moderator explicitly restores.';
    if (r?.decision === 'REVIEW') return 'Recommended action: Moderator should review and then Approve/Reject.';
    return 'Recommended action: Safe to keep approved and visible.';
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg p-5 border ${
      isRemoved
        ? 'border-red-200 bg-red-50/30'
        : isRejectedQueue
          ? 'border-red-200 bg-red-50/20'
          : isPendingReview
            ? 'border-amber-200 bg-amber-50/20'
            : 'border-gray-100'
    }`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {/* Author */}
          <div className="flex items-center gap-3 mb-2">
            {post.author?.profilePictures?.[0] && (
              <img
                src={post.author.profilePictures[0]}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
              />
            )}
            <div>
              <span className="font-semibold text-sm">{post.author?.profileName || 'Unknown'}</span>
              <span className="text-xs text-gray-400 ml-2">@{post.author?.username || '—'}</span>
            </div>
            <span className="text-xs text-gray-400 ml-auto">
              {new Date(post.createdAt).toLocaleString()}
            </span>
          </div>

          <div className="mb-2">
            <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${
              isRemoved
                ? 'bg-gray-200 text-gray-700'
                : isRejectedQueue
                  ? 'bg-red-100 text-red-700'
                  : isPendingReview
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
            }`}>
              {isRemoved ? 'DELETED' : moderationStatus}
            </span>
          </div>

          {/* Content */}
          <p className="text-gray-800 text-sm mb-3">{post.content}</p>

          {/* Images */}
          {post.images?.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {post.images.map(img => (
                <div key={img._id} className="rounded-lg border p-2 bg-white">
                  <a href={img.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={img.url}
                      alt="post"
                      className="w-28 h-28 object-cover rounded-md border hover:opacity-80 transition"
                    />
                  </a>
                  <div className="mt-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      !img.moderationStatus || img.moderationStatus === 'APPROVED'
                        ? 'bg-green-100 text-green-700'
                        : img.moderationStatus === 'PENDING_REVIEW'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }`}>
                      {img.moderationStatus || 'APPROVED'}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => onApproveImage(post._id, img._id)}
                      className="px-2 py-1 rounded bg-green-100 text-green-700 text-[10px] font-semibold hover:bg-green-200"
                    >
                      Approve Img
                    </button>
                    <button
                      type="button"
                      onClick={() => onRejectImage(post._id, img._id)}
                      className="px-2 py-1 rounded bg-red-100 text-red-700 text-[10px] font-semibold hover:bg-red-200"
                    >
                      Reject Img
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Moderation info for removed posts */}
          {isRemoved && post.moderationReason && (
            <div className="text-xs bg-red-100 text-red-700 px-3 py-2 rounded-lg inline-block">
              Removed: {post.moderationReason} {post.deletedBy && `by ${post.deletedBy}`}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          {!isRemoved ? (
            <>
              <button
                type="button"
                onClick={onScan}
                disabled={scanning}
                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-200 disabled:opacity-50"
              >
                {scanning ? 'Scanning...' : 'Scan'}
              </button>
              <button
                type="button"
                onClick={onRemove}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  isRejectedQueue
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                {isRejectedQueue ? 'Delete Permanently' : 'Reject'}
              </button>
              {(isPendingReview || isRejectedQueue) && (
                <button
                  type="button"
                  onClick={onRestore}
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200"
                >
                  Approve
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={onRestore}
              className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200"
            >
              Restore
            </button>
          )}
        </div>
      </div>

      {/* Scan Results */}
      {scanResult && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border text-xs">
          <strong className="block mb-1">Scan Results:</strong>
          {scanResult.length === 0 ? (
            <span className="text-gray-500">No images to scan</span>
          ) : (
            <div className="space-y-2">
              {scanResult.map((r, idx) => (
                <div key={idx} className="rounded-md border bg-white p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${
                      r?.decision === 'REJECT'
                        ? 'text-red-700'
                        : r?.decision === 'REVIEW'
                          ? 'text-amber-700'
                          : 'text-green-700'
                    }`}>
                      {r?.decision === 'REJECT' ? '❌ REJECT' : r?.decision === 'REVIEW' ? '⚠️ REVIEW' : '✅ APPROVE'}
                    </span>
                    <span className="text-gray-500">
                      Risk Score: <strong>{r?.riskScore ?? 0}</strong>/100
                    </span>
                    <span className="text-gray-500">
                      Priority: <strong>{r?.reviewPriority || 'N/A'}</strong>
                    </span>
                    <span className="text-gray-400 ml-auto">{r.processingTime}</span>
                  </div>

                  <div className="text-gray-700">
                    <strong>Why:</strong> {humanDecisionSummary(r)}
                  </div>

                  <div className="text-gray-700">
                    <strong>Policy:</strong> {r?.policyVersion || 'N/A'}
                  </div>

                  {Array.isArray(r?.matchedPolicyLabels) && r.matchedPolicyLabels.length > 0 ? (
                    <div className="text-gray-700">
                      <strong>Matched Labels:</strong>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {r.matchedPolicyLabels.map((label, labelIdx) => (
                          <span
                            key={`${label.label}-${labelIdx}`}
                            className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 border"
                            title={`${label.label} (${label.confidence ?? 0}%)`}
                          >
                            {label.label} - {label.confidence ?? 0}% ({label.action || 'matched'})
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500">No policy label matches recorded.</div>
                  )}

                  <div className="text-gray-600 italic">{actionHint(r)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommunityModeration;
